import Indicator from '../Indicator';
import subpixel from '../util/canvasSubpixelOffset';

/** High/Low indicator */
export default class HighLowIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {boolean} noClip - Sets default clipping
	 * @property {object[]} inputs - Indicator inputs. Ex { name: 'extentType', value: 'auto' | 'ohlc' | 'close }
	 */
	_getDefaultParams() {
		return {
			id: 'highlow',
			name: 'High / Low',
			description: 'Label the high/low for current chart.',
			noClip: true,
			inputs: [
				{ name: 'extentType', value: 'auto' } // auto, ohlc or close
			]
		};
	}

	/** @returns {null} */
	getRangeMin() {
		return null;
	}

	/** @returns {null} */
	getRangeMax() {
		return null;
	}

	/** @returns {null} */
	_getMarkers() {
		return {};
	}

	/** @returns {null} */
	_getDataMap() {
		return null;
	}

	/**
	 * The top and bottom extents will differ depending on the markertype of the price indicator.
	 * Line/fill charts should look at the close while ohlc/candlestick charts should use low and high.
	 * @returns {string} The type of extent minimum to use
	 */
	_getExtentMin() {
		const extentType = this.params.inputs[0].value;

		if (
			extentType === 'auto' &&
			this.panel.indicators[0].params.id === 'price'
		) {
			return /candle|ohlc/.test(
				this.panel.indicators[0].params.markerType
			)
				? 'low'
				: 'close';
		} else {
			return extentType === 'ohlc' ? 'low' : 'close';
		}
	}

	/**
	 * The top and bottom extents will differ depending on the markertype of the price indicator.
	 * Line/fill charts should look at the close while ohlc/candlestick charts should use low and high.
	 * @returns {string} The type of extent maximum to use
	 */
	_getExtentMax() {
		const extentType = this.params.inputs[0].value;

		if (
			extentType === 'auto' &&
			this.panel.indicators[0].params.id === 'price'
		) {
			return /candle|ohlc/.test(
				this.panel.indicators[0].params.markerType
			)
				? 'high'
				: 'close';
		} else {
			return extentType === 'ohlc' ? 'high' : 'close';
		}
	}

	/**
	 * Gets a reference to the previous close indicator on this panel. This will
	 * allow us to call the _getDimensions method to enable collision detection.
	 * @returns {Indicator} Panel previous close indicator
	 */
	_getPreviousCloseIndicator() {
		for (let x = 0, xLen = this.panel.indicators.length; x < xLen; x++) {
			if (this.panel.indicators[x].params.id === 'previousclose') {
				return this.panel.indicators[x];
			}
		}
	}

	/**
	 * Calculates the extent for the high/low indicator
	 * @param {object} data - Indicator data
	 * @returns {object} Indicator extent
	 * @property {number} minValue - Symbol minimum price
	 * @property {number} maxValue - Symbol maximum price
	 * @property {string} minFormat - Formatted minimum symbol price
	 * @property {string} maxFormat - Formatted maximum symbol price
	 * @property {string} minDate - Date of the symbol's lowest price
	 * @property {string} maxDate - Date of the symbol's highest price
	 * @property {number} maxY - Adjusted y axis maximum
	 * @property {number} minY - Adjusted y axis minimum
	 * @property {number} maxX - Adjusted x axis maximum
	 * @property {number} minX - Adjusted x axis minimum
	 */
	_getExtent(data) {
		const { core } = this;
		let maxValue = -Infinity;
		let minValue = Infinity;
		let maxDate = new Date();
		let minDate = new Date();
		const rectSize = 10;
		const priceData = [...data.price];
		const minDatapoint = this._getExtentMin();
		const maxDatapoint = this._getExtentMax();
		const formatString = this.panel.yAxis
			.getFormatString(this.panel.yAxis.primaryScale, 5)
			.replace(/s/, 'f');
		const limitLeft = this.panel.size.padding.left;
		const limitRight = limitLeft + this.panel.size.width;

		// if previous point is returned, do not include it in the range for high/low indicator CH-3095
		if (this.panel.core.params.returnPreviousPoint) {
			priceData.shift();
		}

		for (let x = 0, xLen = priceData.length; x < xLen; x++) {
			// discard points that are clipped out
			if (
				this.xScale(core.closestExchangeIndex(priceData[x].date)) >
				limitRight
			) {
				continue;
			}
			if (
				this.xScale(core.closestExchangeIndex(priceData[x].date)) <
				limitLeft
			) {
				continue;
			}

			maxValue = Math.max(priceData[x][maxDatapoint], maxValue);
			minValue = Math.min(priceData[x][minDatapoint], minValue);

			if (priceData[x][maxDatapoint] === maxValue) {
				maxDate = priceData[x].date;
			}

			if (priceData[x][minDatapoint] === minValue) {
				minDate = priceData[x].date;
			}
		}

		const maxY = this.yScale(maxValue);
		const minY = this.yScale(minValue);
		const maxX = this.xScale(core.closestExchangeIndex(maxDate));
		const minX = this.xScale(core.closestExchangeIndex(minDate));
		const minFormat = this.panel.yAxis.getFormatValue(minValue, formatString);
		const maxFormat = this.panel.yAxis.getFormatValue(maxValue, formatString);

		return {
			minValue,
			maxValue,
			minFormat,
			maxFormat,
			minDate,
			maxDate,
			maxY: maxY - rectSize - 4,
			minY: minY + 4,
			maxX,
			minX,
			data: {
				high: {
					date: maxDate,
					value: maxValue,
					valueFormatted: maxFormat,
					x: maxX,
					y: maxY
				},
				low: {
					date: minDate,
					value: minValue,
					valueFormatted: minFormat,
					x: minX,
					y: minY
				}
			}
		};
	}

	/**
	 * Determines how text should be aligned based on label width and content
	 * @param {number} val - X axis value
	 * @param {number} width - Text label width
	 * @returns {string} 'left' | 'right' | 'center'
	 */
	_getTextAlignment(val, width) {
		if (val - width <= this.panel.size.padding.left) {
			return 'left';
		} else if (
			val + width >
			this.panel.size.padding.left + this.panel.size.width
		) {
			return 'right';
		}

		return 'center';
	}

	/**
	 * Renders a label on the panel
	 * @param {CanvasRenderingContext2D} ctx - Chart canvas context
	 * @param {string} val - Label value
	 * @param {number} x - X axis location to draw label
	 * @param {number} y - Y axis location to draw laabel
	 * @param {string} color - Label color
	 */
	_drawLabel(ctx, val, x, y, color) {
		this.panel.glowText(
			ctx,
			val,
			subpixel.px(x),
			subpixel.px(y - 1) + 11.5,
			color,
			this.params.style.label.glow.color
		);
	}

	/**
	 * Draws a high low icon on the panel
	 * @param {CanvasRenderingContext2D} ctx - Chart canvas context
	 * @param {string} val - Icon value
	 * @param {number} x - X axis location to draw icon
	 * @param {number} y - Y axis location to draw icon
	 * @param {string} color - Icon color
	 * @param {number} size - Icon size in px
	 */
	_drawIcon(ctx, val, x, y, color, size) {
		ctx.font = '11px Arial';
		ctx.fillStyle = color;
		ctx.strokeStyle = color === '#FFFFFF' ? '#000000' : '#FFFFFF';
		subpixel.fillRect(ctx, x, y, size, size);

		ctx.beginPath();

		// drawing individual lines this way makes for a crisper icon than using antialiased text.
		if (val === 'H') {
			// H
			subpixel.moveTo(ctx, x + 3, y + 2);
			subpixel.lineTo(ctx, x + 3, y + 8);
			subpixel.moveTo(ctx, x + 3, y + 5);
			subpixel.lineTo(ctx, x + 7, y + 5);
			subpixel.moveTo(ctx, x + 7, y + 2);
			subpixel.lineTo(ctx, x + 7, y + 8);
		} else {
			// L
			subpixel.moveTo(ctx, x + 3, y + 2);
			subpixel.lineTo(ctx, x + 3, y + 8);
			subpixel.lineTo(ctx, x + 7, y + 8);
		}

		ctx.stroke();
		ctx.closePath();
	}

	/**
	 * Render High/Low indicator to the panel
	 * @param {CanvasRenderingContext2D} ctx - Chart canvas context
	 * @param {object} data - Symbol data
	 * @param {object} extentData - Extent data from this._getExtent()
	 */
	_renderHighLow(ctx, data, extentData) {
		const { core } = this.panel;
		// if a render listener has been registered, emit and return
		if (core.eventEmitter.listeners('INDICATOR_HIGHLOW_RENDER').length > 0) {
			return core.eventEmitter.emit('INDICATOR_HIGHLOW_RENDER', {
				boundingBox: {
					top: this.panel.size.top,
					right: this.panel.size.left + this.panel.size.width,
					bottom: this.panel.size.top + this.panel.size.height,
					left: this.panel.size.left
				},
				ctx,
				data: extentData.data,
				indicator: this
			});
		}
		let lowY;
		let highY;
		const lowLabel = extentData.minFormat;
		const highLabel = extentData.maxFormat;
		const gap = 3;
		const rectSize = 10;
		const lowWidth = ctx.measureText(highLabel).width + rectSize + gap;
		const highWidth = ctx.measureText(lowLabel).width + rectSize + gap;
		const lowAlign = this._getTextAlignment(extentData.minX, lowWidth / 2);
		const highAlign = this._getTextAlignment(extentData.maxX, highWidth / 2);
		const lowColor = this.params.style.min.color;
		const highColor = this.params.style.max.color;
		let highIntersect = false;
		let lowIntersect = false;
		const prevCloseIndicator = this._getPreviousCloseIndicator();
		const prevCloseDimensions =
				prevCloseIndicator && data.previousclose
					? prevCloseIndicator._getDimensions(
						data.previousclose[data.previousclose.length - 1],
						data.previousclose.length - 1,
						data.previousclose
					)
					: {};

		ctx.textAlign = 'left';
		ctx.textBaseline = 'bottom';

		// high
		const highX =
			highAlign === 'left'
				? extentData.maxX
				: highAlign === 'center'
					? extentData.maxX - lowWidth / 2
					: extentData.maxX - lowWidth;
		highY = extentData.maxY;

		if (prevCloseIndicator) {
			highIntersect = prevCloseIndicator.intersect(
				{ x: highX, y: highY, width: highWidth, height: rectSize },
				prevCloseDimensions
			);

			if (highIntersect) {
				highY = prevCloseDimensions.y - rectSize - 1;
			}
		}

		this._drawIcon(ctx, 'H', highX, highY, highColor, rectSize);
		this._drawLabel(
			ctx,
			extentData.maxFormat,
			highX + rectSize + gap,
			highY + 1,
			highColor
		);

		// low
		const lowX =
			lowAlign === 'left'
				? extentData.minX
				: lowAlign === 'center'
					? extentData.minX - lowWidth / 2
					: extentData.minX - lowWidth;
		lowY = extentData.minY;

		if (prevCloseIndicator) {
			lowIntersect = prevCloseIndicator.intersect(
				{ x: lowX, y: lowY, width: lowWidth, height: rectSize },
				prevCloseDimensions
			);

			if (lowIntersect) {
				lowY = prevCloseDimensions.y + prevCloseDimensions.height + 1;
			}
		}

		this._drawIcon(ctx, 'L', lowX, lowY, lowColor, rectSize);
		this._drawLabel(
			ctx,
			extentData.minFormat,
			lowX + rectSize + gap,
			lowY + 1,
			lowColor
		);
	}

	/**
	 * @param {object} data Symbol data object
	 * @returns {undefined}
	 */
	render(data) {
		if (!data.price) return;

		const extentData = this._getExtent(data);

		if (extentData.minValue === extentData.maxValue) {
			return;
		} else {
			return this._renderHighLow(this.panel.rootContext, data, extentData);
		}
	}
}
